import type React from 'react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useLayoutEffect} from '@github-ui/use-layout-effect'
import {testIdProps} from '@github-ui/test-id-props'
import {Box} from '@primer/react'
import {StyledInput} from './StyledInput'
import {useFilterQuery, useInput, useSuggestions} from '../context'

type FilterInputProps = {
  id: string
  hasValidationMessage?: boolean
  placeholder?: string
  onKeyDown?: React.KeyboardEventHandler<Element>
}

export const Input = ({
  id,
  hasValidationMessage = false,
  placeholder = 'Search or filter',
  onKeyDown,
}: FilterInputProps) => {
  const {rawFilterRef} = useFilterQuery()
  const {suggestionsVisible, activeSuggestion} = useSuggestions()
  const {
    caretRef,
    inputKeyDown,
    inputOnBlur,
    inputOnChange,
    inputOnFocus,
    inputRef,
    inputSelectionEnd,
    inputSelectionStart,
    updateInputSelection,
  } = useInput()
  const sizerRef = useRef<HTMLDivElement>(null)
  const [inputScrollLeft, setInputScrollLeft] = useState(0)
  const styledInputContainerRef = useRef<HTMLDivElement>(null)

  const checkCursorPosition = useCallback(
    (el: HTMLInputElement) => {
      updateInputSelection(el.selectionStart ?? 0, el.selectionEnd ?? 0)
    },
    [updateInputSelection],
  )

  const onKeyDownHandler = useCallback<React.KeyboardEventHandler<HTMLInputElement>>(
    e => {
      checkCursorPosition(e.currentTarget as HTMLInputElement)
      inputKeyDown(e)
      onKeyDown?.(e)
    },
    [checkCursorPosition, inputKeyDown, onKeyDown],
  )

  const onClickHandler = useCallback<React.MouseEventHandler<HTMLInputElement>>(
    e => {
      checkCursorPosition(e.currentTarget as HTMLInputElement)
    },
    [checkCursorPosition],
  )

  const inputWidth = useMemo(() => {
    const minWidth = 100
    const currentSizerScrollWidth = sizerRef.current?.scrollWidth ?? 0
    const newInputWidth = Math.max(currentSizerScrollWidth + 2, minWidth)

    return `${newInputWidth}px`

    // This adds a subscriptions to recalculate in the event of state changes that impact the input width
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputSelectionStart, sizerRef.current?.scrollWidth, rawFilterRef?.current])

  useLayoutEffect(() => {
    const minWidth = 100

    const cursor = caretRef.current
    const styledInputContainer = styledInputContainerRef.current

    if (cursor && styledInputContainer) {
      //If the cursor is out of view to the left
      if (cursor.offsetLeft < inputScrollLeft) {
        setInputScrollLeft(cursor.offsetLeft - styledInputContainer.clientWidth - minWidth)
        //If the cursor is out of view to the right
      } else if (cursor.offsetLeft > inputScrollLeft + styledInputContainer.clientWidth) {
        setInputScrollLeft(cursor.offsetLeft - styledInputContainer.clientWidth + minWidth)
      } else {
        styledInputContainer.scrollLeft = inputScrollLeft
      }
    }
  }, [caretRef, inputScrollLeft, inputSelectionStart, rawFilterRef])

  const shadowInput = useMemo(() => {
    return (
      <>
        <span>
          {inputSelectionStart ? rawFilterRef?.current.substring(0, inputSelectionStart) : rawFilterRef?.current}
        </span>
        <span {...testIdProps('filter-cursor')} ref={caretRef} />
        <span>{inputSelectionStart && rawFilterRef?.current.substring(inputSelectionStart)}</span>
      </>
    )
  }, [caretRef, inputSelectionStart, rawFilterRef])

  useEffect(() => {
    if (inputRef.current && inputSelectionStart > -1) {
      inputRef.current.selectionStart = inputSelectionStart
      inputRef.current.selectionEnd = inputSelectionEnd
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef, inputSelectionStart, inputSelectionEnd, rawFilterRef?.current])

  return (
    <Box
      {...testIdProps('styled-input-container')}
      ref={styledInputContainerRef}
      tabIndex={-1}
      className="styled-input-container"
      sx={{
        position: 'relative',
        display: 'flex',
        overflowX: 'auto',
        overflowY: 'hidden',
        fontSize: 'inherit',
        alignItems: 'center',
        scrollbarWidth: 'none',
        flex: 1,
        alignSelf: 'stretch',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}
    >
      <Box
        {...testIdProps('styled-input-content')}
        aria-hidden="true"
        className="styled-input-content"
        sx={{
          position: 'absolute',
          display: 'inline-flex',
          padding: 0,
          wordBreak: 'break-word',
          whiteSpace: 'pre',
          userSelect: 'none',
          flex: 1,
        }}
      >
        {/* This is a visual-only component and is intentionally hidden from screen readers.
        It will use the Input below instead */}
        <StyledInput />
      </Box>
      <Box {...testIdProps('filter-input-wrapper')} sx={{height: '30px', width: '100%', alignSelf: 'stretch'}}>
        <Box
          {...testIdProps('filter-sizer')}
          ref={sizerRef}
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: 0,
            overflow: 'scroll',
            whiteSpace: 'pre',
            visibility: 'hidden',
          }}
        >
          {shadowInput}
        </Box>
        <Box
          id={`${id}-input`}
          as="input"
          role="combobox"
          aria-expanded={suggestionsVisible}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-controls={`${id}-suggestions`}
          aria-activedescendant={
            activeSuggestion !== null && suggestionsVisible ? `suggestion-${activeSuggestion}` : undefined
          }
          aria-describedby={hasValidationMessage ? `${id}-validation-message` : ''}
          placeholder={placeholder}
          ref={inputRef}
          value={rawFilterRef?.current}
          onFocus={inputOnFocus}
          onBlur={inputOnBlur}
          onKeyDown={onKeyDownHandler}
          onClick={onClickHandler}
          onChange={inputOnChange}
          name={`${id}-inputname`}
          autoComplete="off"
          spellCheck="false"
          className="FormControl-input"
          sx={{
            height: '30px',
            width: inputWidth,
            position: 'relative',
            display: 'flex',
            minWidth: '100%',
            p: 0,
            overflowX: 'auto',
            overflowY: 'hidden',
            color: 'transparent',
            resize: 'none',
            background: 'transparent',
            border: 0,
            caretColor: 'var(--fgColor-default, var(--color-fg-default))',
            ':focus': {
              outline: 'none',
              boxShadow: 'none !important',
            },
          }}
          {...testIdProps('filter-input')}
        />
      </Box>
    </Box>
  )
}

try{ Input.displayName ||= 'Input' } catch {}